import Modal from 'react-modal';
import "../../styles/AdminPage.css";
import Button, { BUTTON_TYPES } from '../Button';
import TextInput from '../inputs/TextInput';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { FaTimes } from "react-icons/fa";
import Autocomplete from '../inputs/AutoComplete';
import toast from 'react-hot-toast';
import { postNewCommittee } from '../../api/admin/AdminDashboardAPI';

Modal.setAppElement('#root');

const CommitteeCreateModal = ({ isOpen, onClose, initialFormData, onFormDataChange, members, addNewCommittee }) => {
    const [name, setName] = useState(initialFormData?.name || "");
    const [heads, setHeads] = useState(initialFormData?.heads || initialFormData?.committeHeads || "");

    const didInit = useRef(false);

    const suggestions = members.map(member =>
        `${member.firstName} ${member.lastName} (${member.ritUserID})`
    );

    const onFormDataChangeRef = useRef(onFormDataChange);
    useEffect(() => {
        onFormDataChangeRef.current = onFormDataChange;
    }, [onFormDataChange]);

    useEffect(() => {
        if (isOpen) {
            if (!didInit.current) {
                setName(initialFormData?.name || "");
                setHeads(initialFormData?.heads || initialFormData?.committeHeads || "");
                didInit.current = true;
            }
        } else {
            didInit.current = false;
        }
    }, [isOpen, initialFormData]);

    useEffect(() => {
        if (onFormDataChangeRef.current) {
            onFormDataChangeRef.current({ name, heads });
        }
    }, [name, heads]);

    const handleReset = () => {
        setName("");
        setHeads("");
    };

    const getMatchingHeads = () => {
        const headNames = heads
            .split(',')
            // remove any “(…)” and then trim
            .map(name =>
                name
                    .replace(/\s*\([^)]*\)\s*/g, '')  // ← kill anything in parentheses (and the extra spaces)
                    .trim()
            )
            // throw away any empty strings
            .filter(name => name.length > 0);

        const nameToMemberMap = new Map(
            members.map(member => [
                `${member.firstName} ${member.lastName}`.toLowerCase(),
                member
            ])
        );

        return headNames
            .map(name => nameToMemberMap.get(name.toLowerCase()))
            .filter(Boolean);
    };

    const submitCommittee = async () => {
        const rawHeadNames = heads
            .split(',')
            .map(n =>
                // remove any “(… )” plus surrounding spaces, then trim
                n.replace(/\s*\([^)]*\)\s*/g, '').trim()
            )
            .filter(n => n.length > 0);

        const nameToMemberMap = new Map(
            members.map(m => [
                `${m.firstName} ${m.lastName}`.toLowerCase(),
                m
            ])
        );

        const seen = new Set();
        const duplicates = rawHeadNames.filter(n => {
            const key = n.toLowerCase();
            if (seen.has(key)) return true;
            seen.add(key);
            return false;
        });
        if (duplicates.length) {
            toast.error(
                `Duplicate head name${duplicates.length > 1 ? 's' : ''}: ${[...new Set(duplicates)].join(', ')}`
            );
            return;
        }

        const notFound = rawHeadNames.filter(n => !nameToMemberMap.has(n.toLowerCase()));
        if (notFound.length) {
            toast.error(
                `Head${notFound.length > 1 ? 's' : ''} not found: ${notFound.join(', ')}`
            );
            return;
        }

        const isCorrectName = name != "" && name != null;

        if (!isCorrectName) {
            toast.error("Committee Name must be filled out");
            return;
        }

        if (name.length > 80) {
            toast.error("Committee Name is too long");
            return;
        }

        try {
            toast.loading("Creating Committee...");
            const jsonBody = {
                committeeName: name,
                heads: getMatchingHeads().map(member => ({ member }))
            };

            const response = await postNewCommittee(jsonBody);
            setTimeout(async () => {
                if (response.ok) {
                    toast.dismiss();
                    try {
                        const newCommittee = await response.json();
                        console.log("Committee Created:", newCommittee);
                        toast.success("Committee created!");
                        addNewCommittee(newCommittee);
                        didInit.current = false;
                        onClose();
                    } catch (jsonError) {
                        console.error("Error parsing JSON:", jsonError);
                        toast.error("Error parsing server response.");
                    }
                } else {
                    toast.dismiss();
                    console.log("Error creating committee. Status:", response.status);
                    toast.error("Event failed to committee. Status: " + response.status);
                }

            }, 500);
        } catch (error) {
            toast.dismiss();
            console.error("Error submitting committee:", error);
            alert("Something went wrong. Please check your connection and try again.");
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Committee Create Modal"
            className="custom-modal-content"
            overlayClassName="custom-modal-overlay"
        >
            <>
                <div className="d-flex flex-row justify-content-between align-items-center mb-3">
                    <h2 className="text-start mb-0">Create Committee</h2>
                    <button className="close-btn" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>
                <div className="form-field w-75">
                    <TextInput
                        id="committeeNameCreate"
                        placeholder="Committee Name"
                        className="form-input"
                        charLimit={200}
                        label="Committee Name"
                        value={name}
                        setValue={(e) => setName(e.target.value)}
                        required
                    />
                </div>
                <div className="form-field mt-3 w-75">
                    <Autocomplete
                        suggestions={suggestions}
                        value={heads}
                        label={"Committee Head(s)"}
                        onChange={setHeads}
                    />
                </div>
                <div className="d-flex flex-row justify-content-center align-items-center two-button-container mt-3">
                    <div className="me-3">
                        <Button
                            id="reset-button"
                            type={BUTTON_TYPES.outline}
                            buttonText="Reset"
                            action={handleReset}
                        />
                    </div>
                    <Button
                        id="filter-button-modal"
                        type={BUTTON_TYPES.primary}
                        buttonText="Create"
                        action={submitCommittee}
                    />
                </div>
            </>
        </Modal>
    );
};

CommitteeCreateModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    initialFormData: PropTypes.object,
    onFormDataChange: PropTypes.func,
};

export default CommitteeCreateModal;
